import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExampleLinkWorkflow } from '../../../api/cafAutomation';
import { CafCorpusEntry, CafLooksLike, getCafCorpusEntry } from '../../../api/cafCorpus';
import { useExampleLinks } from '../automation/useExampleLinks';
import { formatCount } from '../format';
import { Icon } from '../Icon';
import { lenderFor, senderEmail, useLenderRules } from '../lenders';
import { useToast } from '../ToastContext';
import { ByLenderView } from './ByLenderView';
import { EditDrawer } from './EditDrawer';
import { ExampleDrawer } from './ExampleDrawer';
import { RerunModal, SwitchOffModal, SwitchOnModal } from './ExampleModals';
import { ExampleAction, ExamplesTable } from './ExamplesTable';
import { GroupByHandModal } from './GroupByHandModal';
import { GroupDrawer, LabelledGroup } from './GroupDrawer';
import { HistoryDrawer } from './HistoryDrawer';
import { OneEmailModal } from './OneEmailModal';
import { QueueTable } from './QueueTable';
import { buildSenderOptions } from './senderOptions';
import { buildLenderMap, exampleMatches, groupMatches, lenderOptions } from './teachFilter';
import { NEEDS_FINAL_RESPONSE_SETTING } from '../copy';
import { useFinalResponseSupported } from '../finalResponseSupport';
import { useTeachData } from './useTeachData';

/** "412" while every page has loaded, "412+" while more are still out there -- never a bare
 *  number that quietly stops counting at the page size. */
const withMore = (count: number, hasMore: boolean) => `${formatCount(count)}${hasMore ? '+' : ''}`;

export interface TeachFocus {
  sender?: string;
  preferredCategory?: 'approved';
  exampleId?: string;
  /** Required alongside exampleId: the backend's GET /entries/<id> needs the sender partition. */
  exampleSender?: string;
  groupId?: string;
}

type Open =
  | { kind: 'group'; id: string; like?: CafLooksLike }
  | { kind: 'example' | 'edit' | 'history' | 'off' | 'on' | 'rerun'; entry: CafCorpusEntry }
  | { kind: 'gbh' }
  | { kind: 'one' }
  | null;

interface Props {
  onQueueCount: (count: number | null) => void;
  focus: TeachFocus | null;
  onFocusHandled: () => void;
  canAutomate?: boolean;
  onOpenWorkflow?: (workflowId: string) => void;
}

export const TeachTab: React.FC<Props> = ({ onQueueCount, focus, onFocusHandled, canAutomate = false, onOpenWorkflow }) => {
  const data = useTeachData();
  const { reload } = data;
  const links = useExampleLinks(true);
  const linkData = links.status === 'ready' ? links.data : null;
  const workflowOf = useCallback((entry: CafCorpusEntry): ExampleLinkWorkflow | null => {
    const row = linkData ? linkData.links[entry.id] : undefined;
    if (!linkData || !row || !row.workflow_id) return null;
    return linkData.workflows.find((item) => item.workflow_id === row.workflow_id) ?? null;
  }, [linkData]);
  const openWorkflow = canAutomate ? onOpenWorkflow : undefined;
  const toast = useToast();
  const rules = useLenderRules();
  const [query, setQuery] = useState('');
  const [lender, setLender] = useState('all');
  const [needsSetting, setNeedsSetting] = useState(false);
  // Final fix wave C2: no example can carry the setting on an older backend, so no filter for it there.
  const askSetting = useFinalResponseSupported();
  const onlyNeedsSetting = askSetting && needsSetting;
  const [view, setView] = useState<'list' | 'lender'>('list');
  const [category, setCategory] = useState<'queue' | 'approved' | 'off'>('queue');
  const pendingSender = useRef<string | null>(null);
  const pendingCategory = useRef<'approved' | null>(null);
  const chooseCategory = (next: 'queue' | 'approved' | 'off') => {
    pendingSender.current = null;
    setCategory(next);
  };
  const [open, setOpen] = useState<Open>(null);
  const [flash, setFlash] = useState<{ kind: 'group' | 'example'; id: string } | null>(null);
  // Final wave item 26: every "open this example" -- the jump from How it's doing and "Show it"
  // after a save in Teach -- takes this one path (see the effect below).
  const [exampleTarget, setExampleTarget] = useState<{ id: string; sender?: string } | null>(null);
  const close = useCallback(() => setOpen(null), []);

  // First load never landed (an empty queue/list would say so with 0, not with silence): keep the
  // banner's retry as the only recovery, and never claim a count we don't actually have.
  const firstLoadFailed = Boolean(data.problem) && data.queueTotal == null;

  useEffect(() => {
    const unknown = data.loading || (Boolean(data.problem) && data.queueTotal == null);
    onQueueCount(unknown ? null : data.queueTotal ?? data.queue.length);
  }, [data.loading, data.problem, data.queueTotal, data.queue.length, onQueueCount]);

  useEffect(() => {
    if (!focus?.sender) return;
    pendingSender.current = senderEmail(focus.sender);
    pendingCategory.current = focus.preferredCategory ?? null;
    setQuery(pendingSender.current);
    setLender('all');
    setView('list');
    onFocusHandled();
  }, [focus, onFocusHandled]);

  useEffect(() => {
    if (!focus?.groupId) return;
    pendingSender.current = null;
    setCategory('queue');
    setView('list');
    setQuery('');
    setLender('all');
    setFlash({ kind: 'group', id: focus.groupId });
    void reload();
    onFocusHandled();
  }, [focus, onFocusHandled, reload]);

  useEffect(() => {
    if (!focus?.exampleId) return;
    pendingSender.current = null;
    setExampleTarget({ id: focus.exampleId, sender: focus.exampleSender });
    onFocusHandled();
  }, [focus, onFocusHandled]);

  useEffect(() => {
    const sender = pendingSender.current;
    if (!sender || data.loading || firstLoadFailed) return;
    // Apply a sender jump once, after the initial lists arrive. Later pages
    // and reloads must not override a tab the person has chosen themselves.
    pendingSender.current = null;
    const preferred = pendingCategory.current;
    pendingCategory.current = null;
    const matches = (row: { sender: string }) => senderEmail(row.sender) === sender;
    setCategory(preferred === 'approved' && data.examples.some(matches) ? 'approved'
      : preferred === 'approved' && data.offSupported && data.switchedOff.some(matches) ? 'off'
      : data.queue.some(matches) ? 'queue' : data.examples.some(matches) ? 'approved'
      : data.offSupported && data.switchedOff.some(matches) ? 'off' : 'queue');
  }, [focus, data.loading, firstLoadFailed, data.queue, data.examples, data.switchedOff, data.offSupported]);

  // Task 24 fix 10: "Show example" opens the drawer directly rather than just flashing a row.
  // Waits for the tab's own load (the initial one on a fresh mount, or the reload a save just
  // started) so "already among the loaded rows" is meaningful and the row is the fresh one.
  useEffect(() => {
    if (!exampleTarget || data.loading) return;
    const { id, sender } = exampleTarget;
    setExampleTarget(null);
    const found = data.examples.find((candidate) => candidate.id === id)
      ?? data.switchedOff.find((candidate) => candidate.id === id);
    if (found) {
      setCategory(found.active === false ? 'off' : 'approved');
      setOpen({ kind: 'example', entry: found });
    } else if (sender) {
      // The backend requires the sender partition on this lookup -- every real caller supplies
      // the sender alongside the id, but a missing one is treated the same as a failed fetch.
      getCafCorpusEntry(id, sender).then(
        (fetched) => { setCategory(fetched.active === false ? 'off' : 'approved'); setOpen({ kind: 'example', entry: fetched }); },
        () => toast.failed("Couldn't open that example."),
      );
    } else {
      toast.failed("Couldn't open that example.");
    }
  }, [exampleTarget, data.loading, data.examples, data.switchedOff, toast]);

  useEffect(() => {
    if (!flash) return undefined;
    const element = flash.kind === 'group'
      ? Array.from(document.querySelectorAll<HTMLElement>('[data-cur-group-id]'))
        .find((row) => row.dataset.curGroupId === flash.id)
      : document.getElementById(`cur-${flash.kind}-${flash.id}`);
    if (element && typeof element.scrollIntoView === 'function') element.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const timer = window.setTimeout(() => setFlash(null), 2600);
    return () => window.clearTimeout(timer);
  }, [flash, data.examples, data.queue]);

  const allSenders = useMemo(() => [
    ...data.queue.map((group) => group.sender),
    ...data.examples.map((entry) => entry.sender),
    ...data.switchedOff.map((entry) => entry.sender),
  ], [data.queue, data.examples, data.switchedOff]);
  // Resolved once per (senders, rules) change, not once per row per render.
  const lenderMap = useMemo(() => buildLenderMap(allSenders, rules), [allSenders, rules]);
  const lenderOf = useCallback(
    (sender: string) => (lenderMap.has(sender) ? lenderMap.get(sender) ?? null : lenderFor(sender, rules)),
    [lenderMap, rules],
  );
  const lenders = useMemo(() => lenderOptions(allSenders, lenderOf), [allSenders, lenderOf]);
  const senders = useMemo(() => buildSenderOptions(allSenders, rules), [allSenders, rules]);
  const queueRows = data.queue.filter((group) => groupMatches(group, query, lender, lenderOf));
  const exampleRows = (category === 'off' ? data.switchedOff : data.examples)
    .filter((entry) => exampleMatches(entry, query, lender, lenderOf, onlyNeedsSetting));
  const usingWorkflow = linkData ? data.examples.filter((entry) => workflowOf(entry) !== null).length : 0;
  const filtered = Boolean(query.trim()) || lender !== 'all';
  const queueIncomplete = data.queueCursor !== null || (data.queueTotal !== null && data.queueTotal > data.queue.length);
  const queueCount = !filtered && data.queueTotal !== null ? data.queueTotal : queueRows.length;
  const examplesIncomplete = (category === 'off' ? data.switchedOffNext : data.examplesNext) !== null;

  const onAction = (action: ExampleAction, entry: CafCorpusEntry) =>
    setOpen(action === 'group' ? { kind: 'example', entry } : { kind: action, entry });
  const onLabelled = (result: LabelledGroup) => {
    if (result.settled) data.dropFromQueue(result.clusterId, result.sender);
    void reload();
  };
  const showExample = (id: string, sender?: string) => {
    setCategory('approved');
    setView('list');
    setQuery('');
    setLender('all');
    setFlash({ kind: 'example', id });
    setExampleTarget({ id, sender });
  };
  const refreshAfter = () => { void reload(); };

  return (
    <div>
      <div className="cur-toolbar">
        <div className="cur-search">
          <Icon name="search" />
          <input className="cur-input" type="search" aria-label="Search" placeholder="Search lender, sender or subject"
            value={query} onChange={(event) => { pendingSender.current = null; setQuery(event.target.value); }} />
        </div>
        <select className="cur-input" aria-label="Lender" value={lender} onChange={(event) => { pendingSender.current = null; setLender(event.target.value); }}>
          <option value="all">All lenders</option>
          {lenders.map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
        {askSetting && (
          <label className="cur-check">
            <input type="checkbox" checked={needsSetting} onChange={(event) => setNeedsSetting(event.target.checked)} />
            <span>{NEEDS_FINAL_RESPONSE_SETTING}</span>
          </label>
        )}
        <div className="cur-seg" role="group" aria-label="Layout">
          <button type="button" aria-pressed={view === 'list'} onClick={() => setView('list')}>All together</button>
          <button type="button" aria-pressed={view === 'lender'} onClick={() => setView('lender')}>By lender</button>
        </div>
        <span className="cur-spacer" />
        <button type="button" className="ui-btn ui-btn--secondary ui-btn--md" onClick={() => setOpen({ kind: 'one' })}>
          <Icon name="mail" size="sm" />Add one email as an example
        </button>
        <button type="button" className="ui-btn ui-btn--secondary ui-btn--md" onClick={() => setOpen({ kind: 'gbh' })}>
          <Icon name="plus" size="sm" />Group emails by hand
        </button>
      </div>

      {data.problem && (
        <div className="cur-alert cur-alert--error" role="alert" style={{ marginBottom: 16 }}>
          <span className="cur-grow">{data.problem}</span>
          <button type="button" className="ui-btn ui-btn--danger ui-btn--sm" onClick={() => { void reload(); }}>Try again</button>
        </div>
      )}

      <div className="cur-seg" role="tablist" aria-label="Teaching categories" style={{ marginBottom: 16 }}>
        {(['queue', 'approved', 'off'] as const).map((key, index, keys) => {
          const label = key === 'queue' ? 'To label' : key === 'approved' ? 'Approved examples' : 'Switched off';
          const count = key === 'queue' ? queueCount : key === 'approved'
            ? data.examples.filter((entry) => exampleMatches(entry, query, lender, lenderOf, onlyNeedsSetting)).length
            : data.switchedOff.filter((entry) => exampleMatches(entry, query, lender, lenderOf, onlyNeedsSetting)).length;
          const more = key === 'queue' ? queueIncomplete : key === 'approved' ? data.examplesNext !== null : data.switchedOffNext !== null;
          return <button type="button" role="tab" key={key} id={`teach-tab-${key}`} aria-controls="teach-category-panel"
            aria-selected={category === key} tabIndex={category === key ? 0 : -1}
            onClick={() => chooseCategory(key)} onKeyDown={(event) => {
              const next = event.key === 'ArrowRight' ? (index + 1) % keys.length : event.key === 'ArrowLeft' ? (index + keys.length - 1) % keys.length
                : event.key === 'Home' ? 0 : event.key === 'End' ? keys.length - 1 : null;
              if (next !== null) { event.preventDefault(); chooseCategory(keys[next]); document.getElementById(`teach-tab-${keys[next]}`)?.focus(); }
            }}>{label} <span className="cur-tab-count">{data.loading || firstLoadFailed || (key === 'off' && !data.offSupported) ? '—' : withMore(count, more)}</span></button>;
        })}
      </div>
      {category === 'queue' && queueIncomplete && !data.queueCursor && !data.loading && <p className="cur-hint">Some groups have not loaded. Refresh to check the current queue.</p>}
      <div id="teach-category-panel" role="tabpanel" aria-labelledby={`teach-tab-${category}`}>
      {firstLoadFailed && view === 'lender' ? null : data.loading && view === 'lender' ? <div className="cur-empty">Loading…</div> : category === 'off' && !data.offSupported && !data.loading
        ? <div className="cur-empty">Switched-off examples are unavailable. Try refreshing.</div>
        : view === 'list' ? (
        <div className="cur-stack">
          {category === 'queue' && <section className="ui-card" aria-label="To label">
            <div className="ui-card__header">
              <div>
                <h2 className="ui-card__title">To label <span className="cur-tab-count">{firstLoadFailed || data.loading ? '—' : withMore(queueCount, queueIncomplete)}</span></h2>
                <p className="cur-card-sub">Groups of similar emails from one sender, waiting for someone to say what should happen.</p>
              </div>
            </div>
            {firstLoadFailed ? null : data.loading && !data.queue.length
              ? <div className="cur-empty">Loading…</div>
              : <QueueTable rows={queueRows} lenderOf={lenderOf} flashId={flash?.kind === 'group' ? flash.id : null}
                  onOpen={(id) => setOpen({ kind: 'group', id })} onLabelLike={(id, like) => setOpen({ kind: 'group', id, like })}
                  empty={<><strong>{queueIncomplete ? 'No matches on the pages loaded so far.' : filtered ? 'Nothing to label here.' : 'Nothing to label.'}</strong>
                    {!queueIncomplete && 'New groups appear each morning.'}</>} />}
            {data.queueCursor && (
              <div className="cur-more-row">
                <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => { void data.moreQueue(); }}>Show more</button>
              </div>
            )}
          </section>}
          {category !== 'queue' && <section className="ui-card" aria-label={category === 'off' ? 'Switched off' : 'Approved examples'}>
            <div className="ui-card__header">
              <div>
                <h2 className="ui-card__title">
                  {category === 'off' ? 'Switched off' : 'Approved examples'}
                </h2>
                <p className="cur-card-sub">
                  {category === 'off' ? 'These examples are kept for reference and are not used by the AI.' : `What the AI copies. When a new email looks like one of these, it does what the example says.${linkData ? ` ${formatCount(usingWorkflow)} of them use a workflow.` : ''}`}
                </p>
              </div>

            </div>
            {firstLoadFailed ? null : data.loading && !exampleRows.length
              ? <div className="cur-empty">Loading…</div>
              : <ExamplesTable rows={exampleRows} ariaLabel={category === 'off' ? 'Switched off' : 'Approved examples'} lenderOf={lenderOf} canSwitchOn={data.offSupported}
                  flashId={flash?.kind === 'example' ? flash.id : null}
                  workflowOf={linkData ? workflowOf : undefined} onOpenWorkflow={openWorkflow}
                  onOpen={(entry) => setOpen({ kind: 'example', entry })} onAction={onAction}
                  empty={<><strong>{examplesIncomplete ? 'No matches on the pages loaded so far.' : category === 'off' ? 'No switched-off examples here.' : filtered ? 'No approved examples here yet.' : 'No approved examples yet.'}</strong>
                    {!examplesIncomplete && category !== 'off' && 'Label a group to create the first one.'}</>} />}
            {(category === 'off' ? data.switchedOffNext : data.examplesNext) !== null && (
              <div className="cur-more-row">
                <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => { void (category === 'off' ? data.moreSwitchedOff() : data.moreExamples()); }}>Show more</button>
              </div>
            )}
          </section>}
        </div>
      ) : (
        <ByLenderView category={category} queue={category === 'queue' ? queueRows : []} examples={category === 'queue' ? [] : exampleRows} lenderOf={lenderOf}
          canSwitchOn={data.offSupported} examplesNext={category === 'queue' ? null : category === 'off' ? data.switchedOffNext : data.examplesNext} queueCursor={category === 'queue' ? data.queueCursor : null}
          onMoreExamples={() => { void (category === 'off' ? data.moreSwitchedOff() : data.moreExamples()); }} onMoreQueue={() => { void data.moreQueue(); }}
          onOpenGroup={(id) => setOpen({ kind: 'group', id })} onLabelLike={(id, like) => setOpen({ kind: 'group', id, like })}
          onOpenExample={(entry) => setOpen({ kind: 'example', entry })} onAction={onAction} />
      )}

      </div>

      {open?.kind === 'group' && (
        <GroupDrawer key={open.id} clusterId={open.id} lenderOf={lenderOf} onClose={close} onLabelled={onLabelled} onShowExample={showExample}
          likeExample={open.like ? { entryId: open.like.entry_id, sender: open.like.sender } : null} />
      )}
      {open?.kind === 'example' && (
        <ExampleDrawer entry={open.entry} lenderOf={lenderOf} onClose={close}
          workflow={linkData ? workflowOf(open.entry) : undefined} onOpenWorkflow={openWorkflow}
          onEdit={(entry) => setOpen({ kind: 'edit', entry })} onHistory={(entry) => setOpen({ kind: 'history', entry })} />
      )}
      {open?.kind === 'edit' && (
        <EditDrawer entry={open.entry} lenderOf={lenderOf} onClose={close} links={linkData} canAutomate={canAutomate}
          onLinked={links.reload} onOpenWorkflow={openWorkflow}
          onSaved={(updated) => { refreshAfter(); if (updated) setFlash({ kind: 'example', id: updated.id }); }} />
      )}
      {open?.kind === 'history' && <HistoryDrawer entry={open.entry} lenderOf={lenderOf} onClose={close} />}
      {open?.kind === 'off' && <SwitchOffModal entry={open.entry} lenderOf={lenderOf} onClose={close} onDone={refreshAfter} />}
      {open?.kind === 'on' && <SwitchOnModal entry={open.entry} lenderOf={lenderOf} onClose={close} onDone={refreshAfter} />}
      {open?.kind === 'rerun' && <RerunModal entry={open.entry} lenderOf={lenderOf} onClose={close} />}
      {open?.kind === 'gbh' && (
        <GroupByHandModal senders={senders} onClose={close} onCreated={refreshAfter}
          onShow={(target) => (target.kind === 'example' ? showExample(target.id, target.sender) : (setCategory('queue'), setView('list'), setQuery(''), setLender('all'), setFlash({ kind: 'group', id: target.id })))} />
      )}
      {open?.kind === 'one' && <OneEmailModal senders={senders} onClose={close} onSaved={refreshAfter} onShowExample={showExample} />}
    </div>
  );
};
